import EditableField from '@wandb/weave/common/components/EditableField';
import * as Tooltip from '@wandb/weave/components/RadixTooltip';
import {RunState} from '@wandb/weave/components/RunState';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {type ReactElement, useState} from 'react';
import {Link} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import type * as FilterTypes from '../../util/filterTypes';
import {getSweepDisplayName} from '../../util/getSweepDisplayName';
import * as Run from '../../util/runs';
import {getRunStateForRun} from '../../util/runs';
import type * as RunTypes from '../../util/runTypes';
import {type WBTableRowWithRunSetInfo} from '../../util/tablecols';
import * as urls from '../../util/urls';
import {TimeDisplay} from '../elements/TimeDisplay';
import {useRunSelectorFeatureDisabled} from './RunSelectorFeaturesContext';
import {RunSelectorIssuesCell} from './RunSelectorIssuesCell';
import {useBaselineRun} from './RunSelectorRunsContext';
import {RunSelectorTagCell} from './RunSelectorTagCell';
import type {WBTableColumn} from './WBTable/types';
import {WBTableCellCreatedAt} from './WBTable/WBTableCellCreatedAt';
import {WBTableCellDefault} from './WBTable/WBTableCellDefault';

type AddFilterFn = (
  key: RunTypes.Key,
  op: FilterTypes.ValueOp,
  value: RunTypes.Value
) => void;

type RenderCellProps = {
  addFilter?: AddFilterFn;
  column: WBTableColumn;
  entityName: string;
  isGroup: boolean;
  isHoveringRow: boolean;
  isHoveringColumn: boolean;
  onSaveRowNotes: (newNotes: string) => void;
  projectName: string;
  row: WBTableRowWithRunSetInfo;
};

const CELL_FILTER_BLACKLIST: RunTypes.Key[] = [
  {section: 'run', name: 'github'},
  {section: 'run', name: 'runInfo.gpu'},
  {section: 'run', name: 'runInfo.gpuCount'},
];

export function RunSelectorCell({
  addFilter,
  column,
  entityName,
  isGroup,
  isHoveringColumn,
  isHoveringRow,
  onSaveRowNotes,
  projectName,
  row,
}: RenderCellProps): ReactElement {
  'use memo';
  const [isNotesTooltipOpen, setIsNotesTooltipOpen] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const baselineRun = useBaselineRun();
  const isBaselineRun = Boolean(baselineRun && baselineRun.id === row.id);

  const isDisabled = useRunSelectorFeatureDisabled();
  const isGroupRun = isGroup && row.group === row.name;
  const columnKey = column.key;
  const value = Run.getValue(row, columnKey);
  if (CELL_FILTER_BLACKLIST.some(f => Run.keysEqual(columnKey, f))) {
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={value}
        isHoveringRow={isHoveringRow}
        isHoveringColumn={isHoveringColumn}
        isBaselineRun={isBaselineRun}
      />
    );
  }

  if (columnKey.section === 'tags') {
    return (
      <RunSelectorTagCell
        entityName={entityName}
        isGroup={isGroup}
        isGroupRun={isGroupRun}
        isHoveringRow={isHoveringRow}
        onAddFilter={addFilter}
        projectName={projectName}
        row={row}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'issues'})) {
    if (isGroup) {
      return <></>;
    }

    return (
      <RunSelectorIssuesCell
        row={row}
        entityName={entityName}
        projectName={projectName}
      />
    );
  }

  // multiple distinct values on grouped rows: state as status pills, the rest
  // as truncated comma text (single-value rows fall through to the cases below)
  if (
    isGroup &&
    columnKey.section === 'run' &&
    Run.MULTI_VALUE_METADATA_COLUMNS.has(columnKey.name)
  ) {
    const values = row.multiValueMetadataColumns?.[columnKey.name];
    if (values != null && values.length > 1) {
      return (
        <WBTableCellDefault
          columnKey={columnKey}
          cellValue={values.join(', ')}
          isBaselineRun={isBaselineRun}
          isHoveringRow={isHoveringRow}
          isHoveringColumn={isHoveringColumn}>
          {columnKey.name === 'state' ? (
            <MultiValueStateCell values={values} />
          ) : (
            <MultiValueTextCell values={values} />
          )}
        </WBTableCellDefault>
      );
    }
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'state'})) {
    // single state (individual runs or single-state groups)
    const state = getRunStateForRun(row);
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={state}
        isBaselineRun={isBaselineRun}
        isHoveringRow={isHoveringRow}
        isHoveringColumn={isHoveringColumn}
        onAddFilter={addFilter}>
        <RunState value={state} />
      </WBTableCellDefault>
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'createdAt'})) {
    return (
      <WBTableCellCreatedAt createdAt={row.createdAt} addFilter={addFilter} />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'heartbeatAt'})) {
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={Run.formatTimestamp(row.heartbeatAt)}
        isBaselineRun={isBaselineRun}
        isHoveringRow={isHoveringRow}
        isHoveringColumn={isHoveringColumn}
        onAddFilter={addFilter}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'updatedAt'})) {
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={Run.formatTimestamp(row.updatedAt)}
        isBaselineRun={isBaselineRun}
        isHoveringRow={isHoveringRow}
        isHoveringColumn={isHoveringColumn}
        onAddFilter={addFilter}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'duration'})) {
    return (
      <TimeDisplay
        timestamp={new Date(row.createdAt)}
        format="month_round"
        now={new Date(row.heartbeatAt)}
      />
    );
  }

  if (Run.keysEqual(columnKey, {section: 'run', name: 'notes'})) {
    const isNotesReadOnly =
      (isGroup && !isGroupRun) || isDisabled('edit-run-notes');
    return (
      <Tooltip.Provider>
        <Tooltip.Root
          open={isNotesTooltipOpen && !isEditingNotes && row.notes.length > 0}
          onOpenChange={setIsNotesTooltipOpen}>
          <TailwindContents>
            <Tooltip.Trigger
              className={twMerge(
                'bg-none border-none p-0',
                isEditingNotes ? 'w-full' : 'max-w-full'
              )}>
              <EditableField
                className="runs-table-editable-field"
                data-test="add-notes-input"
                placeholder={isNotesReadOnly ? '-' : 'Add notes...'}
                readOnly={isNotesReadOnly}
                save={onSaveRowNotes}
                value={row.notes || ''}
                setIsEditing={isEditing => {
                  setIsEditingNotes(isEditing);
                  setIsNotesTooltipOpen(false); // prevents tooltip from opening right after an edit
                }}
              />
            </Tooltip.Trigger>
          </TailwindContents>
          <Tooltip.Portal>
            <Tooltip.Content className="max-w-[350px]">
              {row.notes}
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  }

  if (
    Run.keysEqual(columnKey, {
      section: 'run',
      name: 'sweep',
    })
  ) {
    const sweep = row.sweep;
    const sweepName = sweep?.name;
    if (!sweep || !sweepName) {
      return <>-</>;
    }

    if (isDisabled('runLinks')) {
      return (
        <WBTableCellDefault
          columnKey={columnKey}
          cellValue={getSweepDisplayName({...sweep, name: sweepName})}
          isBaselineRun={isBaselineRun}
          isHoveringRow={isHoveringRow}
          isHoveringColumn={isHoveringColumn}
          onAddFilter={addFilter}
        />
      );
    }

    return (
      <Link
        to={urls.sweep({
          entityName,
          projectName,
          sweepName: sweepName,
        })}>
        {getSweepDisplayName({...sweep, name: sweepName})}
      </Link>
    );
  }

  if (isGroup && Array.isArray(value)) {
    const strValues = value.map(v =>
      v != null && typeof v === 'object' ? JSON.stringify(v) : String(v)
    );
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={strValues.join(', ')}
        isBaselineRun={isBaselineRun}
        isHoveringRow={isHoveringRow}
        isHoveringColumn={isHoveringColumn}>
        <MultiValueTextCell values={strValues} />
      </WBTableCellDefault>
    );
  }

  if (value != null) {
    return (
      <WBTableCellDefault
        columnKey={columnKey}
        cellValue={value}
        isBaselineRun={isBaselineRun}
        isHoveringRow={isHoveringRow}
        isHoveringColumn={isHoveringColumn}
        onAddFilter={addFilter}
      />
    );
  }

  return <>-</>;
}

// shared truncation: cap visible values and flag when more exist
const splitMultiValue = (
  values: string[]
): {visible: string[]; hasMore: boolean} => ({
  visible: values.slice(0, Run.MULTI_VALUE_VISIBLE),
  hasMore: values.length > Run.MULTI_VALUE_VISIBLE,
});

const MultiValueTextCell = ({values}: {values: string[]}): ReactElement => {
  'use memo';
  const {visible, hasMore} = splitMultiValue(values);
  const text = visible.join(', ') + (hasMore ? ', +' : '');
  return (
    <TailwindContents>
      <div className="truncate min-w-0">{text}</div>
    </TailwindContents>
  );
};

const MultiValueStateCell = ({values}: {values: string[]}): ReactElement => {
  'use memo';
  const {visible, hasMore} = splitMultiValue(values);
  return (
    <TailwindContents>
      <div className="inline-flex items-center gap-2 flex-nowrap whitespace-nowrap">
        {visible.map(value => (
          <div key={value} className="inline-block">
            <RunState value={value} />
          </div>
        ))}
        {hasMore && <div className="text-wb-text-tertiary">+</div>}
      </div>
    </TailwindContents>
  );
};
